import classnames from "classnames";
import React, { useRef, useState } from "react";
import { useOutsideDetector } from "src/hooks";
import theme from "src/theme";
import styled from "styled-components";

const Label = styled.div`
  display: flex;
  align-items: center;
  padding: ${theme.margins.paddingPage};
`;

const Wrapper = styled.div`
  cursor: pointer;

  &:hover > ${Label} {
    opacity: 0.7;
  }
`;

const OptionsWrapper = styled.ul`
  position: absolute;
  top: 36px;
  left: 1px;
  padding: 10px 0;
  list-style: none;
  border: 1px solid ${theme.colors.veryLightGrey};
  background-color: ${theme.colors.white};
  width: 100%;
`;

const Option = styled.li`
  padding: 10px 20px;
  white-space: pre-wrap;

  &:hover {
    background-color: ${theme.colors.whiteSmoke};
  }

  &.active {
    font-weight: 700;
  }
`;

interface IOption {
  label: string | number;
}

interface IProps<T> {
  label: JSX.Element | string;
  onChange?: (option: T) => void;
  options: T[];
  defaultOption?: number;
  className?: string;
}

const Select = <T extends IOption>({
  label,
  options,
  defaultOption = 0,
  onChange,
  className,
}: IProps<T>): JSX.Element => {
  /**
   * Handle list opening
   */
  const [listOpen, setListOpen] = useState<boolean>(false);

  // Toggle/Close options list
  const toggleList = (): void => setListOpen(!listOpen);
  const closeList = (): void => setListOpen(false);

  // Close options list when click is detected outside of wrapper
  const wrapperRef = useRef<HTMLDivElement>(null);
  useOutsideDetector([wrapperRef], closeList);

  /**
   * Handle selected option
   */
  const [selectedOption, setSelectedOption] = useState<number>(defaultOption);

  /**
   * When an option is selected, update selected value,
   * share new quantity, and close options wrapper
   */
  const handleSelectedOption = (option: T, index: number): void => {
    setSelectedOption(index);
    onChange?.(option);
    toggleList();
  };

  return (
    <Wrapper
      className={classnames("select-wrapper", className)}
      onClick={toggleList}
      ref={wrapperRef}
    >
      <Label className="select-label">{label}</Label>

      {listOpen && (
        <OptionsWrapper className="select-options">
          {options.map(
            (option, i: number): JSX.Element => (
              <Option
                className={classnames("select-option", {
                  active: selectedOption === i,
                })}
                onClick={(): void => handleSelectedOption(option, i)}
                key={i}
              >
                {option.label}
              </Option>
            )
          )}
        </OptionsWrapper>
      )}
    </Wrapper>
  );
};

export default Select;
